import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { isNonRetryableSdkError } from '../../errors/policy/retry-policy.js';
import { getHttpResponseStatus } from '../../transport/http-request.js';
import { connectivityStore } from '../connectivity-store.js';
import { getCachedQuery } from '../internal/get-cached-query.js';
import { OUTAGE_POLL_INTERVALS_MS, pollingResponseHash } from './policy.js';

export interface PollingState {
  lastRestAt: number;
  authBlocked: boolean;
  lastHash: string | null;
  adaptiveStep: number;
  cooldownUntil: number;
  errorCycles: number;
  recovering: boolean;
  wireReady: boolean;
  batched: boolean;
  recoveryEpoch: number;
  recoveryScope: string | undefined;
}

const states = new WeakMap<Query, PollingState>();
const responseHashes = new WeakMap<Query, string>();
const listeners = new WeakMap<QueryClient, Set<(query: Query) => void>>();

export function pollingState(query: Query): PollingState {
  let state = states.get(query);
  if (state === undefined) {
    state = {
      lastRestAt: 0,
      authBlocked: false,
      lastHash: null,
      adaptiveStep: 0,
      cooldownUntil: 0,
      errorCycles: 0,
      recovering: false,
      wireReady: false,
      batched: false,
      recoveryEpoch: 0,
      recoveryScope: undefined,
    };
    states.set(query, state);
  }
  return state;
}

export function subscribePollingState(
  client: QueryClient,
  listener: (query: Query) => void,
): () => void {
  let set = listeners.get(client);
  if (set === undefined) {
    set = new Set();
    listeners.set(client, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

export async function observePollingFetch<T>(
  client: QueryClient,
  key: QueryKey,
  runQuery: () => Promise<T>,
  wireReady = false,
  signal?: AbortSignal,
): Promise<T> {
  const query = getCachedQuery(client, { queryKey: key });
  if (query === undefined) return runQuery();
  const state = pollingState(query);
  const epoch = state.recoveryEpoch;
  responseHashes.delete(query);
  try {
    const response = await runQuery();
    state.authBlocked = getHttpResponseStatus(signal) === 401;
    if (state.authBlocked) return response;
    const hash = responseHashes.get(query) ?? pollingResponseHash(response);
    state.adaptiveStep =
      state.lastHash === hash
        ? Math.min(state.adaptiveStep + 1, OUTAGE_POLL_INTERVALS_MS.length - 1)
        : 0;
    state.lastHash = hash;
    state.lastRestAt = Date.now();
    state.errorCycles = 0;
    state.cooldownUntil = 0;
    state.wireReady = wireReady && epoch === state.recoveryEpoch;
    if (state.wireReady && connectivityStore.getState().state === 'CONNECTED')
      state.recovering = false;
    return response;
  } catch (error) {
    if (
      error !== null &&
      typeof error === 'object' &&
      'name' in error &&
      error.name === 'AbortError'
    )
      throw error;
    const fields =
      error !== null && typeof error === 'object'
        ? (error as { retryAfterMs?: unknown; category?: unknown; httpStatus?: unknown })
        : {};
    state.errorCycles += 1;
    const delay = Math.min(1_000 * 2 ** Math.min(state.errorCycles, 6), 60_000);
    const retryAfter = typeof fields.retryAfterMs === 'number' ? fields.retryAfterMs : 0;
    state.cooldownUntil = Date.now() + Math.max(delay, retryAfter);
    throw error;
  } finally {
    responseHashes.delete(query);
    for (const listener of listeners.get(client) ?? []) listener(query);
  }
}

export function pollingBlocked(query: Query): boolean {
  if (pollingState(query).authBlocked) return true;
  const error = query.state.error;
  if (query.state.status !== 'error') return false;
  if (isNonRetryableSdkError(error)) return true;
  if (error === null || typeof error !== 'object') return false;
  const fields = error as { category?: unknown; httpStatus?: unknown };
  return fields.category === 'auth' || fields.httpStatus === 404 || fields.httpStatus === 410;
}

export function consumePollingBatch(client: QueryClient, key: QueryKey): boolean {
  const query = getCachedQuery(client, { queryKey: key });
  if (query === undefined) return false;
  const state = pollingState(query);
  const batched = state.batched;
  state.batched = false;
  return batched;
}

/** Capture domain fields before normalization replaces rows with entity IDs. */
export function capturePollingResponse(
  client: QueryClient,
  key: QueryKey,
  response: unknown,
): void {
  const query = getCachedQuery(client, { queryKey: key });
  if (query !== undefined) responseHashes.set(query, pollingResponseHash(response));
}

export function requirePollingRecovery(
  client: QueryClient,
  key: QueryKey,
  recoveryScope?: string,
): void {
  const query = getCachedQuery(client, { queryKey: key });
  if (query === undefined) return;
  const state = pollingState(query);
  if (state.recovering && !state.wireReady && state.recoveryScope === recoveryScope) return;
  state.recoveryScope = recoveryScope;
  state.recovering = true;
  state.wireReady = false;
  state.recoveryEpoch += 1;
  for (const listener of listeners.get(client) ?? []) listener(query);
}
